'use client';

import { useMemo, useState } from 'react';

import { ArrowRight, CircleHelp } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';

import { AddOnCardView } from '@/components/booking/shared/AddOnCard';
import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { WindowCleaningConfigurator } from '@/components/services/window-cleaning/WindowCleaningConfigurator';
import { Button } from '@/components/ui/button';
import { usePublicPricing } from '@/hooks/use-public-pricing';
import { Link } from '@/i18n/routing';
import { BOOKING_ADD_ONS } from '@/lib/pricing/addOnsCatalog';
import {
  calculateWindowCleaningEstimate,
  createEmptyWindowTypeQuantities,
  WINDOW_TYPE_IDS,
  type WindowPricingMode,
  type WindowTypeId,
  type WindowTypeQuantities,
} from '@/lib/pricing/window-cleaning-calculator';

/** Add-ons offered next to a window cleaning visit, with their quantity cap. */
const WINDOW_ADD_ONS: Array<{ id: string; max: number }> = [
  { id: 'balcony_cleaning', max: 3 },
  { id: 'sofa_two_seater', max: 5 },
  { id: 'oven_cleaning', max: 3 },
];

const ADD_ON_CATALOG_BY_ID = new Map(BOOKING_ADD_ONS.map(addOn => [addOn.id, addOn]));

/** Same add-on definitions and card design as the booking calculator. */
const ADD_ON_DEFS = WINDOW_ADD_ONS.flatMap(({ id, max }) => {
  const definition = ADD_ON_CATALOG_BY_ID.get(id);
  return definition ? [{ ...definition, max }] : [];
});

function formatMoney(locale: string, amount: number): string {
  const intlLocale = locale === 'bg' ? 'bg-BG' : locale === 'de' ? 'de-DE' : 'en-GB';
  return new Intl.NumberFormat(intlLocale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function clampBlinds(windows: number, blinds: number): number {
  return Math.min(Math.max(0, blinds), Math.max(0, windows));
}

export function WindowCleaningCalculator() {
  const t = useTranslations('servicesPage.windowCleaningCalculator');
  const locale = useLocale();
  const reducedMotion = useReducedMotion();
  const pricing = usePublicPricing();
  const windowPricing = pricing.windowPricing;

  const [mode, setMode] = useState<WindowPricingMode>('type');
  const [types, setTypes] = useState<WindowTypeQuantities>(() => createEmptyWindowTypeQuantities());
  const [windowSqm, setWindowSqm] = useState(0);
  const [blindsSqm, setBlindsSqm] = useState(0);
  const [afterRenovation, setAfterRenovation] = useState(false);
  const [addOnQty, setAddOnQty] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');

  const estimate = useMemo(
    () =>
      calculateWindowCleaningEstimate({
        mode,
        types,
        area: { windowSqm, blindsSqm },
        afterRenovation,
        pricePerWindow: windowPricing.pricePerWindow,
        minimumWindows: windowPricing.minimumWindows,
        minimumPrice: windowPricing.minimumPrice,
        addOns: ADD_ON_DEFS.map(def => ({
          id: def.id,
          quantity: addOnQty[def.id] ?? 0,
          unitPrice: pricing.addOnsPricing?.[def.id] ?? def.price,
        })),
      }),
    [
      mode,
      types,
      windowSqm,
      blindsSqm,
      afterRenovation,
      addOnQty,
      pricing.addOnsPricing,
      windowPricing.pricePerWindow,
      windowPricing.minimumWindows,
      windowPricing.minimumPrice,
    ],
  );

  const bookingHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set('service', 'window_cleaning');
    if (!estimate.isEmpty) {
      params.set('windows', String(estimate.bookedWindows));
    }
    if (comment.trim()) {
      params.set('notes', comment.trim().slice(0, 280));
    }
    return `/book?${params.toString()}`;
  }, [estimate.bookedWindows, estimate.isEmpty, comment]);

  const updateType = (id: WindowTypeId, field: 'windows' | 'blinds', value: number) => {
    setTypes(prev => {
      const nextWindows = field === 'windows' ? Math.max(0, value) : prev[id].windows;
      const nextBlinds =
        field === 'blinds'
          ? clampBlinds(nextWindows, value)
          : clampBlinds(nextWindows, prev[id].blinds);
      return {
        ...prev,
        [id]: { windows: nextWindows, blinds: nextBlinds },
      };
    });
  };

  const typeTitles = useMemo(
    () =>
      WINDOW_TYPE_IDS.reduce(
        (acc, id) => {
          acc[id] = t(`types.${id}.title`);
          return acc;
        },
        {} as Record<WindowTypeId, string>,
      ),
    [t],
  );

  const addOnCards = (
    <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:gap-4'>
      {ADD_ON_DEFS.map(def => {
        const qty = addOnQty[def.id] ?? 0;
        const setQuantity = (next: number) =>
          setAddOnQty(prev => ({
            ...prev,
            [def.id]: Math.max(0, Math.min(def.max, next)),
          }));
        const name = t(`addOns.items.${def.id}`);

        return (
          <AddOnCardView
            key={def.id}
            addOn={def}
            quantity={qty}
            max={def.max}
            price={pricing.addOnsPricing?.[def.id] ?? def.price}
            onToggle={() => setQuantity(qty > 0 ? 0 : 1)}
            onIncrement={() => setQuantity(qty + 1)}
            onDecrement={() => setQuantity(qty - 1)}
            labels={{
              name,
              decreaseQuantity: t('addOns.decreaseQuantity', { name }),
              increaseQuantity: t('addOns.increaseQuantity', { name }),
            }}
          />
        );
      })}
    </div>
  );

  return (
    <section
      id='window-calculator'
      className='relative scroll-mt-24 bg-linen py-14 sm:py-20'
      aria-labelledby='window-calculator-heading'
    >
      <UnifiedContainer size='xl' padding='lg'>
        <motion.div
          className='max-w-2xl text-left'
          initial={reducedMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className='text-xs font-semibold uppercase tracking-[0.16em] text-oxford-blue/55'>
            {t('eyebrow')}
          </p>
          <h2
            id='window-calculator-heading'
            className='mt-2 text-2xl font-extrabold tracking-tight text-oxford-blue sm:text-3xl md:text-4xl'
          >
            {t('title')}
          </h2>
          <p className='mt-3 text-base leading-relaxed text-oxford-blue/70 sm:text-lg'>
            {t('subtitle')}
          </p>
          <a
            href='#window-scope'
            className='mt-4 inline-flex items-center gap-2 text-sm font-semibold text-oxford-blue underline-offset-4 hover:underline'
          >
            <CircleHelp className='h-4 w-4 text-dark-spring-green' aria-hidden />
            {t('whatsIncluded')}
          </a>
        </motion.div>

        <div className='mt-10 grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(17rem,0.8fr)] lg:items-start lg:gap-8'>
          <motion.div
            className='rounded-2xl border border-oxford-blue/10 bg-white p-5 shadow-e2 sm:p-7 lg:p-8'
            initial={reducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <WindowCleaningConfigurator
              labels={{
                modeType: t('modes.type'),
                modeArea: t('modes.area'),
                areaHint: t('area.hint'),
                areaWindowLabel: t('area.windowLabel'),
                areaBlindsLabel: t('area.blindsLabel'),
                windowsCount: count => t('windowsCount', { count }),
                blindsCount: count => t('blindsCount', { count }),
                afterRenovation: t('afterRenovation'),
                addOnsTitle: t('addOns.title'),
                commentLabel: t('comment.label'),
                commentPlaceholder: t('comment.placeholder'),
                typeTitles,
              }}
              mode={mode}
              onModeChange={setMode}
              types={types}
              onTypeChange={updateType}
              windowSqm={windowSqm}
              onWindowSqmChange={value => {
                setWindowSqm(value);
                setBlindsSqm(prev => Math.min(prev, value));
              }}
              blindsSqm={blindsSqm}
              onBlindsSqmChange={setBlindsSqm}
              afterRenovation={afterRenovation}
              onAfterRenovationChange={setAfterRenovation}
              addOns={addOnCards}
              comment={comment}
              onCommentChange={setComment}
            />
          </motion.div>

          <motion.aside
            className='lg:sticky lg:top-24'
            initial={reducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.45,
              delay: reducedMotion ? 0 : 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <div className='rounded-2xl bg-oxford-blue p-5 text-seasalt shadow-e3 sm:p-6'>
              <p className='text-xs font-semibold uppercase tracking-[0.12em] text-orange-peel-on-dark'>
                {t('summary.eyebrow')}
              </p>
              <p className='mt-3 text-3xl font-extrabold tabular-nums text-orange-peel-on-dark sm:text-4xl'>
                {estimate.isEmpty && estimate.addOnsNet === 0
                  ? formatMoney(locale, 0)
                  : formatMoney(locale, estimate.totalGross)}
              </p>
              <p className='mt-1 text-sm text-seasalt/70'>{t('summary.vatIncluded')}</p>

              <dl className='mt-5 space-y-2 border-t border-white/15 pt-4 text-sm'>
                {!estimate.isEmpty ? (
                  <>
                    <div className='flex justify-between gap-3'>
                      <dt className='text-seasalt/65'>{t('summary.windows')}</dt>
                      <dd className='font-semibold tabular-nums text-seasalt'>
                        {formatMoney(locale, estimate.windowsNet)}
                      </dd>
                    </div>
                    {estimate.blindsNet > 0 ? (
                      <div className='flex justify-between gap-3'>
                        <dt className='text-seasalt/65'>{t('summary.blinds')}</dt>
                        <dd className='font-semibold tabular-nums text-seasalt'>
                          {formatMoney(locale, estimate.blindsNet)}
                        </dd>
                      </div>
                    ) : null}
                    {estimate.renovationExtraNet > 0 ? (
                      <div className='flex justify-between gap-3'>
                        <dt className='text-seasalt/65'>{t('summary.renovation')}</dt>
                        <dd className='font-semibold tabular-nums text-seasalt'>
                          {formatMoney(locale, estimate.renovationExtraNet)}
                        </dd>
                      </div>
                    ) : null}
                  </>
                ) : null}
                {estimate.addOnsNet > 0 ? (
                  <div className='flex justify-between gap-3'>
                    <dt className='text-seasalt/65'>{t('summary.addOns')}</dt>
                    <dd className='font-semibold tabular-nums text-seasalt'>
                      {formatMoney(locale, estimate.addOnsNet)}
                    </dd>
                  </div>
                ) : null}
                {estimate.vat > 0 ? (
                  <div className='flex justify-between gap-3'>
                    <dt className='text-seasalt/65'>{t('summary.vat')}</dt>
                    <dd className='font-semibold tabular-nums text-seasalt'>
                      {formatMoney(locale, estimate.vat)}
                    </dd>
                  </div>
                ) : null}
                {!estimate.isEmpty ? (
                  <div className='flex justify-between gap-3'>
                    <dt className='text-seasalt/65'>{t('summary.equivalent')}</dt>
                    <dd className='font-semibold tabular-nums text-seasalt'>
                      {t('summary.windowsUnit', {
                        count: estimate.bookedWindows,
                      })}
                    </dd>
                  </div>
                ) : null}
              </dl>

              {!estimate.isEmpty && estimate.equivalentWindows < windowPricing.minimumWindows ? (
                <p className='mt-3 text-xs leading-relaxed text-orange-peel-on-dark'>
                  {t('summary.minOrder', {
                    min: windowPricing.minimumWindows,
                    price: formatMoney(locale, windowPricing.minimumPrice),
                  })}
                </p>
              ) : null}

              <p className='mt-4 text-xs leading-relaxed text-seasalt/60'>
                {t('summary.disclaimer')}
              </p>

              {estimate.isEmpty && estimate.addOnsNet === 0 ? (
                <Button
                  type='button'
                  size='lg'
                  disabled
                  className='mt-5 h-12 w-full gap-2 bg-orange-peel text-base font-bold text-white'
                >
                  {t('summary.cta')}
                  <ArrowRight className='h-4 w-4' aria-hidden />
                </Button>
              ) : (
                <Button
                  asChild
                  size='lg'
                  className='mt-5 h-12 w-full gap-2 bg-orange-peel text-base font-bold text-white hover:bg-orange-peel/90'
                >
                  <Link href={bookingHref}>
                    {t('summary.cta')}
                    <ArrowRight className='h-4 w-4' aria-hidden />
                  </Link>
                </Button>
              )}
            </div>
          </motion.aside>
        </div>
      </UnifiedContainer>
    </section>
  );
}
